import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CategoryResponse, CategoryRequest, CategoriesService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-category',
    templateUrl: './edit-category.component.html',
    styleUrl: './edit-category.component.scss',
    host: {
        class: 'app-edit-category'
    },
    standalone: false
})
export class EditCategoryComponent extends BaseViewComponent implements OnInit {

  categoryId: number = 0;
  category: CategoryResponse = {};

  filterOperator = FilterOperator;
  categoriesList: any[] = [];
  categoriesTotalRecords: number = 0;
  categoriesRowsPerPageOption: number = 10;

  public categoryForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    hasParentCategory: ['false'],
    parentCategoryId: [[]],
    category: [null]
  },
    { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private categoriesService: CategoriesService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.categoryId = params['categoryId'];

      if (this.categoryId) {
        this.category = await firstValueFrom(this.categoriesService.apiCategoriesIdGet(this.categoryId));

        await this.getCategories({ pageIndex: 1, pageSize: this.categoriesRowsPerPageOption, search: '' });

        this.ensureSelectedParentCategory();
        this.initForm();
      }
    });
  }

  isCategoryRequired(): boolean {
    return this.categoryForm.get('hasParentCategory')?.value === 'true';
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentCategory = formGroup.get('hasParentCategory')?.value;
      const parentCategoryIds = formGroup.get('parentCategoryId')?.value ?? [];

      if (hasParentCategory === 'true' && parentCategoryIds.length === 0) {
        return { requiredCategory: true };
      }
      return null;
    };
  }


  async onLazyLoadCategories(event: any) {
    await this.getCategories(event);
  }

  async getCategories(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.categoriesRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const categoriesPage = await firstValueFrom(this.categoriesService.apiCategoriesFilterComboPost(payload));

    // prevent adding itself as parent
    this.categoriesList = (categoriesPage.results ?? []).filter(c => c.id != this.categoryId);

    this.categoriesTotalRecords = categoriesPage.totalCount ?? 0;

    if (this.category?.parent?.id && this.category.parent.id != this.categoryId) {
      const exists = this.categoriesList.some((c: any) => c.id == this.category.parent?.id);
      if (!exists) {
        const current = await firstValueFrom(this.categoriesService.apiCategoriesComboValueIdGet(this.category.parent.id))
        this.categoriesList = [current, ...this.categoriesList];
      }
    }

  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  initForm() {
    this.categoryForm.controls['name'].setValue(this.category.name);
    this.categoryForm.controls['code'].setValue(this.category.code);
    this.categoryForm.controls['parentCategoryId'].setValue(this.category.parentId);

    if (this.category.parentId) {
      this.categoryForm.controls['hasParentCategory'].setValue('true');
      this.categoryForm.controls['parentCategoryId'].setValue([this.category.parentId]);
    } else {
      this.categoryForm.controls['hasParentCategory'].setValue('false');
      this.categoryForm.controls['parentCategoryId'].setValue([]);
    }

    this.categoryForm.get('hasParentCategory')?.valueChanges.subscribe((hasParent) => {
      if (hasParent === 'false') {
        this.categoryForm.controls['parentCategoryId'].setValue([]);
      }
    });
  }

  private ensureSelectedParentCategory(): void {
    const parentId = this.category.parentId;
    if (!parentId) return;

    // if parentCategory is not in the 1st loaded page
    const exists = this.categoriesList.some((c: any) => c.id === parentId);
    if (!exists) {
      this.categoriesList = [
        {
          id: parentId,
          name: (this.category as any).parent?.name ?? `#${parentId}`
        },
        ...this.categoriesList
      ];
    }
  }

  async submitForm() {
    if (this.categoryForm.valid) {

      const payload: CategoryRequest = {
        name: this.categoryForm.controls['name'].value,
        code: this.categoryForm.controls['code'].value,
        parentId: this.categoryForm.controls['parentCategoryId'].value?.[0] ?? undefined
      };

      await firstValueFrom(this.categoriesService.apiCategoriesUpdateIdPut(this.categoryId, payload));

      this.router.navigate(['/categories', this.categoryId]);
    }
  }
}
